// © 2025 Camp Promethean, LLC. All Rights Reserved.
// Percentile Indicator Component

import { cn } from '@/lib/utils';

interface PercentileIndicatorProps {
  value: number;
  percentile: number;
  label: string;
  maxValue?: number;
  className?: string;
  showValue?: boolean;
  colorScale?: 'green' | 'blue' | 'purple' | 'amber' | 'rose';
}

export function PercentileIndicator({
  value,
  percentile,
  label,
  maxValue = 10,
  className = '',
  showValue = true,
  colorScale = 'green'
}: PercentileIndicatorProps) {
  // Normalize value as percentage of maxValue
  const normalizedValue = Math.min(Math.max(value, 0), maxValue) / maxValue;
  
  // Get color based on percentile
  const getColor = () => {
    const colors = {
      green: {
        low: '#d1fae5',
        medium: '#10b981',
        high: '#047857'
      },
      blue: {
        low: '#dbeafe',
        medium: '#3b82f6',
        high: '#1d4ed8'
      },
      purple: {
        low: '#ede9fe',
        medium: '#8b5cf6',
        high: '#6d28d9'
      },
      amber: {
        low: '#fef3c7',
        medium: '#f59e0b',
        high: '#b45309'
      },
      rose: {
        low: '#fee2e2',
        medium: '#f43f5e',
        high: '#be123c'
      }
    };
    
    if (percentile < 25) return colors[colorScale].low;
    if (percentile < 75) return colors[colorScale].medium;
    return colors[colorScale].high;
  };
  
  // Get descriptive text for percentile
  const getPercentileText = () => {
    if (percentile < 25) return 'Below Average';
    if (percentile < 50) return 'Average';
    if (percentile < 75) return 'Above Average';
    if (percentile < 90) return 'Exceptional';
    return 'Top Tier';
  };
  
  return (
    <div className={cn('space-y-1', className)}>
      <div className="flex justify-between items-center text-sm">
        <span className="font-medium text-gray-700">{label}</span>
        {showValue && (
          <span className="text-gray-500">
            {value}/{maxValue}
          </span>
        )}
      </div>
      
      <div className="relative pt-1">
        {/* Background bar */}
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          {/* Foreground progress bar */}
          <div
            className="h-full rounded-full transition-all duration-300 ease-in-out"
            style={{
              width: `${normalizedValue * 100}%`,
              backgroundColor: getColor()
            }}
          />
        </div>
        
        {/* Percentile indicator */}
        <div className="flex justify-between items-center mt-1">
          <span className="text-xs" style={{ color: getColor() }}>
            {getPercentileText()}
          </span>
          <span className="text-xs font-medium text-gray-500">
            {percentile}th percentile
          </span>
        </div>
      </div>
    </div>
  );
}

interface PercentileGridProps {
  metrics: Array<{
    label: string;
    value: number;
    percentile: number;
    maxValue?: number;
  }>;
  className?: string;
  colorScale?: 'green' | 'blue' | 'purple' | 'amber' | 'rose';
}

export function PercentileGrid({
  metrics,
  className = '',
  colorScale = 'green'
}: PercentileGridProps) {
  return (
    <div className={cn('grid gap-3', className)}>
      {metrics.map((metric, index) => (
        <PercentileIndicator
          key={`${metric.label}-${index}`}
          value={metric.value}
          percentile={metric.percentile}
          label={metric.label}
          maxValue={metric.maxValue || 10}
          colorScale={colorScale}
        />
      ))}
    </div>
  );
} 